import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Paper, Typography, Fab, IconButton, Tooltip } from '@material-ui/core';
import { Add, Delete, Refresh } from '@material-ui/icons';
import { BackButton, hasPermission, localization, hasAtLeastOnePermission, getDimensionsOfPagination } from '../../sc-utilities';
import MaterialTable from 'material-table';
import * as actions from './store/securazeToolsActions';
import * as action from '../../np-core/Auth/store/authActions';
import SecurazeToolDelete from './SecurazeToolDelete';
import Toolbar from '../../sc-utilities/Toolbar'
import { CancelToken } from 'axios';
import { MuiTableRows } from '../../../themes';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const SecurazeToolList = props => {
    const { classes, translations, permissions, deleting, onFetchSecurazeToolList, onFetchSecurazeToolListCount, fetching, saveColumnsFilter, activeCustomerID, onBulkDeleteSecurazeTools, tableColumns, redirectHandler, tableRef } = props;
    const [deleteModalState, setDeleteModalState] = useState({ open: false, securazeToolIDs: [] });
    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []); const countSource = CancelToken.source();
    const listSource = CancelToken.source();

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: hasAtLeastOnePermission(permissions, ['securaze_tool_delete']),
        selectionProps: rowData => {
            return { id: `checkbox_${rowData.SecurazeToolID}` }
        },
        search: false,
        minBodyHeight: window.innerHeight - 197 < 300 ? 300 : window.innerHeight - 197,
        maxBodyHeight: window.innerHeight - 197,
        pageSizeOptions: rowsPageSizeInit,

        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    useEffect(() => {
        return () => {
            countSource.cancel('Operation canceled by the user.');
            listSource.cancel('Operation canceled by the user.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    };

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = q;
        let count = await onFetchSecurazeToolListCount(query, { countSource: countSource });
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count.data)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count.data ? await onFetchSecurazeToolList(query, { listSource: listSource }) : { data: [] };
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result.data, page: query.page, pageSize: pageSize, totalCount: count.data };
    }


    const tableTitle = () => {
        return (<div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>{translations["securaze_tools"] || 'Securaze Tools'}</Typography>
                {
                    hasPermission(permissions, 'securaze_tool_create') ?
                        <Fab
                            variant="extended"
                            size="small"
                            id="create_securazeTool"
                            color="primary"
                            aria-label="Add"
                            style={{ borderRadius: '1px' }}
                            className={classes.fab}
                            onClick={() => redirectHandler(`/downloads/securazeTool/create`)}
                        >
                            <Add size="small" />
                            {translations["create_new"] || "Create new"}
                        </Fab> : null
                }
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh id={'refresh_button'} />
                    </IconButton>
                </Tooltip>
            </div>
        </div>)
    };

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, securazeToolIDs: [] });
    };

    const handleCloseDanger = async () => {
        await onBulkDeleteSecurazeTools({ SecurazeToolIDs: deleteModalState.securazeToolIDs, ActiveCustomerID: activeCustomerID });
        setDeleteModalState({ open: false, securazeToolIDs: [] });
        tableRef.current && tableRef.current.onQueryChange()
    };

    const handleBulkDelete = securazeToolIDs => {
        setDeleteModalState({ ...deleteModalState, open: true, securazeToolIDs: securazeToolIDs });
    };

    // Table Settings
    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["securaze_tools"] || "Securaze Tools"}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(securazeTool => securazeTool.SecurazeToolID))
        }
    ]

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`SecurazeTools`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID })
        return newColumnState
    }

    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                localization={localization(translations)}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {
                hasPermission(permissions, 'securaze_tool_delete') ? (
                    <SecurazeToolDelete
                        activeCustomerID={activeCustomerID}
                        entityName={translations['securaze_tools'] || 'Securaze Tools'}
                        translations={translations}
                        handleCloseDanger={handleCloseDanger}
                        handleCloseSafe={handleCloseSafe}
                        deleting={deleting}
                        deleteModalState={deleteModalState}
                    />) : null
            }
        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || [],
        securazeTool: state.exportImport.securazeTools,
        fetching: state.exportImport.securazeTools.fetching,
        deleting: state.exportImport.securazeTools.deleting
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchSecurazeToolListCount: (filter, options) => dispatch(actions.fetchSecurazeToolsCount(filter, options)),
        onFetchSecurazeToolList: (filter, options) => dispatch(actions.fetchSecurazeTools(filter, options)),
        onBulkDeleteSecurazeTools: data => dispatch(actions.bulkDeleteSecurazeTools(data)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(SecurazeToolList));
